import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import {
  ADD_PRODUCT_URL,
  ONBOARDING_URL,
  PRODUCTS_URL,
  PHOTO_STYLER_URL,
} from 'config/config'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { emailCaptureManager } from 'services/EmailCaptureManager'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import { useCartStore } from 'stores/cartStore/CartStoreProvider'
import { usePhotoBooksStore } from 'stores/RootStoreProvider'
import { setBookQuantity } from 'components/PhotoBook/PhotoBookProvider/PhotoBook.updaters'
import type { CartItem } from 'stores/cartStore/CartItem'
import { ADDRESS_SOURCE } from 'stores/addressStore/address.consts'
import { type CreateOrderParams } from 'services/OrderManager.types'
import ExpressCheckoutButton from 'components/StripeExpressCheckout/ExpressCheckoutButton'
import AddProductRow from 'features/startCreating/components/AddProductRow'
import { getEditRoute } from '../cart.utils'
import { CHECKOUT_ENTRY_POINT } from '../cart.consts'
import { useBagAnalytics } from '../hooks/useBagAnalytics'
import { useGroupCheckoutPricing } from '../hooks/useGroupCheckoutPricing'
import { useFreeShippingMessage } from '../hooks/useFreeShippingMessage'
import { useCheckoutBlockerPopup } from '../hooks/useCheckoutBlockerPopup'
import { useRemoveItemPopup } from '../hooks/useRemoveItemPopup'
import HeaderSegment from './HeaderSegment'
import PromoCodeSegment from './PromoCodeSegment'
import ProductSummarySegment from './ProductSummarySegment/ProductSummarySegment'
import EmptyBag from './EmptyBag'
import CanadaTariffNotice from 'features/canadaTariffs/components/CanadaTariffNotice'
import { CartUpsellsSection } from 'features/cartUpsells/components/CartUpsellsSection'
import * as S from './CartDrawer.styles'

// The overlay exit runs 450ms (mobile drawer) / 320ms (desktop sheet) on a
// front-loaded easing curve, so by this point it has already cleared most of
// its travel. Navigating here overlaps the route change with the tail of the
// slide instead of swapping the page under an overlay that hasn't moved yet.
const NAVIGATE_DURING_CLOSE_MS = 180

type CartDrawerProps = {
  open: boolean
  onClose: () => void
  onCheckout: () => void
  callCreateOrder: (params: Omit<CreateOrderParams, 'bagAnalytics'>) => void
}

function CartDrawer({
  open,
  onClose,
  onCheckout,
  callCreateOrder,
}: CartDrawerProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const cartStore = useCartStore()
  const photoBooksStore = usePhotoBooksStore()
  const experimentManager = useExperimentManager()
  const isPhotoBooksEnabled = experimentManager.isEnabled('photo-books')
  const isCartExpressCheckoutEnabled = experimentManager.isEnabled(
    'cart-express-checkout'
  )

  const cartItems = useMobxSelector(() => cartStore.cartItems, [cartStore])
  const isEmpty = cartItems.length === 0
  const groupPricing = useGroupCheckoutPricing()
  const freeShippingMessage = useFreeShippingMessage()
  const { trackBagEvent } = useBagAnalytics()

  const { popup: checkoutBlockerPopup, openPopupIfBlocked } =
    useCheckoutBlockerPopup({ onClose })
  const { popup: removeItemPopup, requestRemoveConfirmation } =
    useRemoveItemPopup({ onCartEmptied: onClose })

  const closeThenNavigate = useCallback(
    (route: string) => {
      onClose()
      setTimeout(() => navigate(route), NAVIGATE_DURING_CLOSE_MS)
    },
    [navigate, onClose]
  )

  const onEditClick = useCallback(
    (item: CartItem) => {
      const route = getEditRoute(item)
      if (route) {
        closeThenNavigate(route)
      }
    },
    [closeThenNavigate]
  )

  const onAddProductClick = useCallback(() => {
    analytics.track(EVENT_NAMES.CART_ADD_PRODUCT_TAPPED)
    closeThenNavigate(ADD_PRODUCT_URL)
  }, [closeThenNavigate])

  const onQuantityChange = useCallback(
    (item: CartItem, qty: number) => {
      if (item.productType === PRODUCT_TYPES.PHOTO_BOOK) {
        photoBooksStore
          .getOrCreateBook(item.id)
          .updateBook(setBookQuantity(Math.max(1, qty)))
        return
      }

      throw new Error(
        `onQuantityChange not implemented for product type ${item.productType}`
      )
    },
    [photoBooksStore]
  )

  const onCheckoutClick = useCallback(() => {
    if (
      groupPricing.isLoading ||
      !groupPricing.isSuccess ||
      openPopupIfBlocked()
    )
      return
    onCheckout()
  }, [
    groupPricing.isLoading,
    groupPricing.isSuccess,
    openPopupIfBlocked,
    onCheckout,
  ])

  const onShopNowClick = useCallback(() => {
    trackBagEvent(EVENT_NAMES.CART_EMPTY_SHOP_NOW_TAPPED)
    const target = isPhotoBooksEnabled ? PRODUCTS_URL : PHOTO_STYLER_URL
    closeThenNavigate(
      emailCaptureManager.passedAnyOnboarding()
        ? target
        : `${ONBOARDING_URL}?redirectTo=${target}`
    )
  }, [trackBagEvent, closeThenNavigate, isPhotoBooksEnabled])

  const content = isEmpty ? (
    <EmptyBag onShopNowClick={onShopNowClick} />
  ) : (
    <>
      <PromoCodeSegment />
      {experimentManager.isEnabled('canada-tariffs-comms') && (
        <CanadaTariffNotice messageKey="order.canada_tariff_notice_bag" />
      )}
      <ProductSummarySegment
        items={cartItems}
        onEdit={onEditClick}
        onQuantityChange={onQuantityChange}
        onRemove={requestRemoveConfirmation}
      />
      {isPhotoBooksEnabled && <AddProductRow onClick={onAddProductClick} />}
      <CartUpsellsSection
        onUpsellAddedToBag={() => closeThenNavigate(PHOTO_STYLER_URL)}
      />
    </>
  )

  const headerElement = <HeaderSegment onClose={onClose} />

  const footerElement = isEmpty ? undefined : (
    <S.Footer>
      <S.CheckoutButtons>
        {isCartExpressCheckoutEnabled && (
          <ExpressCheckoutButton
            addressSource={ADDRESS_SOURCE.EXPRESS_CHECKOUT}
            shouldBlockOpen={openPopupIfBlocked}
            onOrderSubmitted={onClose}
            callCreateOrder={callCreateOrder}
            isCartFlow
            entryPoint={CHECKOUT_ENTRY_POINT.BAG_EXPRESS_CHECKOUT}
          />
        )}
        <S.CheckoutButton
          onClick={onCheckoutClick}
          disabled={groupPricing.isLoading || !groupPricing.isSuccess}
          loading={groupPricing.isLoading}
          data-testid="cart-checkout-button"
        >
          {t('order.checkout_button_text')}
        </S.CheckoutButton>
      </S.CheckoutButtons>
      {freeShippingMessage && (
        <S.FreeShippingMessage data-testid="cart-free-shipping-message">
          {freeShippingMessage}
        </S.FreeShippingMessage>
      )}
    </S.Footer>
  )

  return (
    <>
      <S.CartSheet
        open={open}
        onOpenChange={(nextOpen: boolean) => {
          if (!nextOpen) onClose()
        }}
        title={t('order.cart_title')}
        header={headerElement}
        footer={footerElement}
        heightMode="tall"
        contentTestId="cart-drawer"
      >
        {content}
      </S.CartSheet>
      {checkoutBlockerPopup}
      {removeItemPopup}
    </>
  )
}

export default CartDrawer
